import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EquipmentPicker } from '@/components/ui/equipment-picker';
import { EquipmentTypePicker } from '@/components/ui/equipment-type-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipmentItem } from '@/features/equipment/queries';
import { useEquipmentType } from '@/features/equipment-types/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  useCreateMaintenancePlan,
  useMaintenancePlan,
  useUpdateMaintenancePlan,
} from '@/features/maintenance-plans/queries';
import {
  maintenancePlanCreateSchema,
  maintenancePlanUpdateSchema,
  type MaintenancePlanCreateValues,
  type MaintenancePlanUpdateValues,
} from '@/features/maintenance-plans/schema';
import type { MaintenancePlan } from '@/features/maintenance-plans/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * MaintenancePlanFormPage — create + edit a plan.
 *
 * Same CreateBranch + EditBranch + FormBody pattern as the
 * Equipment / EquipmentType / WorkOrder forms. The shape split is
 * needed because the create payload carries target fields
 * (equipmentId XOR equipmentTypeId, optional vesselId) that are
 * server-immutable post-creation; the update payload only touches
 * the editable subset.
 *
 * Unlike WorkOrders, plans STAY editable across their whole life
 * — there's no "not editable" gate based on status. Triggers are
 * the moving parts; the plan template itself can be edited any
 * time and only future generations pick up the change.
 */

function TextField<TForm extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
  hint?: string;
  type?: 'text' | 'date' | 'number';
  textarea?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={4}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value}
      </p>
    </div>
  );
}

const WORK_ORDER_TYPES = [
  'preventive',
  'corrective',
  'condition_based',
  'drydock',
  'improvement',
  'inspection',
] as const;
const WORK_ORDER_PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;

/**
 * Target mode toggle: a plan can target either a specific equipment
 * instance OR an equipment type (with optional vessel scoping when
 * the type mode is selected). Mutually exclusive — the schema
 * .refine() enforces this server-side mirror.
 */
type TargetMode = 'equipment' | 'equipmentType';

interface CreateBranchProps {
  defaultVesselId: string | null;
  defaultEquipmentId: string | null;
  defaultEquipmentTypeId: string | null;
}

function CreateBranch({
  defaultVesselId,
  defaultEquipmentId,
  defaultEquipmentTypeId,
}: CreateBranchProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateMaintenancePlan();

  const initialMode: TargetMode = defaultEquipmentTypeId
    ? 'equipmentType'
    : 'equipment';
  const [targetMode, setTargetMode] = useState<TargetMode>(initialMode);

  const form = useForm<MaintenancePlanCreateValues & { _vesselId: string | null }>({
    resolver: zodResolver(maintenancePlanCreateSchema),
    defaultValues: {
      title: '',
      description: null,
      type: 'preventive',
      priority: 'normal',
      estimatedHours: null,
      leadTimeDays: 0,
      equipmentId: defaultEquipmentId ?? null,
      equipmentTypeId: defaultEquipmentTypeId ?? null,
      vesselId: defaultVesselId ?? null,
      _vesselId: defaultVesselId,
    },
  });

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('maintenancePlan.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('maintenancePlan.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    // Strip the local _vesselId scoping field. Then enforce the XOR
    // by clearing whichever target field is NOT in current mode —
    // the schema's .refine() will catch any leakage but this keeps
    // the payload tidy.
    const { _vesselId: _, ...payload } = values;
    void _;
    const cleaned: MaintenancePlanCreateValues =
      targetMode === 'equipment'
        ? { ...payload, equipmentTypeId: null, vesselId: null }
        : { ...payload, equipmentId: null };
    createMut.mutate(cleaned, {
      onSuccess: (created) => navigate(`/maintenance-plans/${created.id}`),
    });
  });

  const watchedVesselId = form.watch('_vesselId');

  // When the user flips target mode we must clear the now-unused
  // target field so the form value stays consistent with the mode.
  const handleModeChange = (next: TargetMode) => {
    setTargetMode(next);
    if (next === 'equipment') {
      form.setValue('equipmentTypeId', null);
      form.setValue('vesselId', null);
    } else {
      form.setValue('equipmentId', null);
    }
  };

  return (
    <FormBody
      mode="create"
      form={form}
      title={t('maintenancePlan.form.createTitle')}
      onSubmit={onSubmit}
      submitting={createMut.isPending}
      submitLabel={t('maintenancePlan.form.submitCreate')}
      cancelTo="/maintenance-plans"
      errorMessage={errorMessage}
      targetMode={targetMode}
      onTargetModeChange={handleModeChange}
      watchedVesselId={watchedVesselId}
    />
  );
}

function EditBranch({ existing }: { existing: MaintenancePlan }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMut = useUpdateMaintenancePlan(existing.id);
  const { data: vessels } = useVessels();
  const { data: equipment } = useEquipmentItem(
    existing.equipmentId ?? undefined,
  );
  const { data: equipmentType } = useEquipmentType(
    existing.equipmentTypeId ?? undefined,
  );

  const initial: MaintenancePlanUpdateValues = {
    title: existing.title,
    description: existing.description,
    priority: existing.priority,
    estimatedHours: existing.estimatedHours,
    leadTimeDays: existing.leadTimeDays,
  };

  const form = useForm<MaintenancePlanUpdateValues>({
    resolver: zodResolver(maintenancePlanUpdateSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.id]);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('maintenancePlan.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('maintenancePlan.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    updateMut.mutate(values, {
      onSuccess: (updated) => navigate(`/maintenance-plans/${updated.id}`),
    });
  });

  const vesselName = existing.vesselId
    ? vessels?.find((v) => v.id === existing.vesselId)
    : null;

  return (
    <FormBody
      mode="edit"
      form={form}
      existing={existing}
      existingEquipmentLabel={
        equipment
          ? `${equipment.tag} — ${equipment.name}`
          : (existing.equipmentId ?? null)
      }
      existingEquipmentTypeLabel={
        equipmentType ? equipmentType.name : (existing.equipmentTypeId ?? null)
      }
      existingVesselLabel={
        vesselName
          ? vesselDisplayName(vesselName)
          : t('maintenancePlan.allVessels')
      }
      title={t('maintenancePlan.form.editTitle')}
      onSubmit={onSubmit}
      submitting={updateMut.isPending}
      submitLabel={t('maintenancePlan.form.submitUpdate')}
      cancelTo={`/maintenance-plans/${existing.id}`}
      errorMessage={errorMessage}
    />
  );
}

type FormBodyProps =
  | {
      mode: 'create';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing?: never;
      existingEquipmentLabel?: never;
      existingEquipmentTypeLabel?: never;
      existingVesselLabel?: never;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      targetMode: TargetMode;
      onTargetModeChange: (mode: TargetMode) => void;
      watchedVesselId: string | null;
    }
  | {
      mode: 'edit';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing: MaintenancePlan;
      existingEquipmentLabel: string | null;
      existingEquipmentTypeLabel: string | null;
      existingVesselLabel: string;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
      targetMode?: never;
      onTargetModeChange?: never;
      watchedVesselId?: never;
    };

function FormBody(props: FormBodyProps) {
  const { t } = useTranslation();
  const {
    title,
    onSubmit,
    submitting,
    submitLabel,
    cancelTo,
    errorMessage,
    mode,
    form,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const control = form.control as Control<any>;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>

      {mode === 'edit' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{t('maintenancePlan.form.immutableHint')}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('maintenancePlan.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('maintenancePlan.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <TextField
                control={control}
                name="title"
                label={t('maintenancePlan.fields.title')}
              />
              <TextField
                control={control}
                name="description"
                label={t('maintenancePlan.fields.description')}
                textarea
              />
            </CardContent>
          </Card>

          {/* Target — only fully visible in create mode; edit shows
              the existing target as ReadOnlyField rows. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('maintenancePlan.detail.sectionTarget')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              {mode === 'create' ? (
                <>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">
                      {t('maintenancePlan.form.targetModeLabel')}
                    </legend>
                    <div className="flex gap-3 text-sm">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="targetMode"
                          checked={props.targetMode === 'equipment'}
                          onChange={() => props.onTargetModeChange('equipment')}
                        />
                        {t('maintenancePlan.form.targetModeEquipment')}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="targetMode"
                          checked={props.targetMode === 'equipmentType'}
                          onChange={() =>
                            props.onTargetModeChange('equipmentType')
                          }
                        />
                        {t('maintenancePlan.form.targetModeType')}
                      </label>
                    </div>
                  </fieldset>

                  {props.targetMode === 'equipment' ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="_vesselId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('maintenancePlan.fields.vesselId')}</FormLabel>
                            <FormControl>
                              <VesselSelect
                                value={field.value ?? null}
                                onChange={(v) => {
                                  field.onChange(v);
                                  form.setValue('equipmentId', null);
                                }}
                                allowEmpty={false}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="equipmentId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('maintenancePlan.fields.equipmentId')}
                            </FormLabel>
                            <FormControl>
                              <EquipmentPicker
                                vesselId={props.watchedVesselId}
                                value={field.value ?? null}
                                onChange={(v) => field.onChange(v)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="equipmentTypeId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('maintenancePlan.fields.equipmentTypeId')}
                            </FormLabel>
                            <FormControl>
                              <EquipmentTypePicker
                                value={field.value ?? null}
                                onChange={(v) => field.onChange(v)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="vesselId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('maintenancePlan.form.optionalVesselScope')}
                            </FormLabel>
                            <FormControl>
                              <VesselSelect
                                value={field.value ?? null}
                                onChange={(v) => field.onChange(v)}
                                allowEmpty
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">
                              {t('maintenancePlan.form.optionalVesselScopeHint')}
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {props.existingEquipmentLabel && (
                    <ReadOnlyField
                      label={t('maintenancePlan.fields.equipmentId')}
                      value={props.existingEquipmentLabel}
                    />
                  )}
                  {props.existingEquipmentTypeLabel && (
                    <ReadOnlyField
                      label={t('maintenancePlan.fields.equipmentTypeId')}
                      value={props.existingEquipmentTypeLabel}
                    />
                  )}
                  <ReadOnlyField
                    label={t('maintenancePlan.fields.vesselId')}
                    value={props.existingVesselLabel}
                  />
                  <ReadOnlyField
                    label={t('maintenancePlan.fields.type')}
                    value={t(`workOrder.type.${props.existing.type}`)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template — type, priority, hours, lead time */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('maintenancePlan.detail.sectionTemplate')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {mode === 'create' && (
                <FormField
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('maintenancePlan.fields.type')}</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {WORK_ORDER_TYPES.map((tp) => (
                            <option key={tp} value={tp}>
                              {t(`workOrder.type.${tp}`)}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('maintenancePlan.fields.priority')}</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {WORK_ORDER_PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {t(`workOrder.priority.${p}`)}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TextField
                control={control}
                name="estimatedHours"
                label={t('maintenancePlan.fields.estimatedHours')}
                hint={t('maintenancePlan.form.estimatedHoursHint')}
              />
              <TextField
                control={control}
                name="leadTimeDays"
                label={t('maintenancePlan.fields.leadTimeDays')}
                type="number"
                hint={t('maintenancePlan.form.leadTimeDaysHint')}
              />
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={submitting}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className={cn('animate-spin')} aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function MaintenancePlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const { data: existing, isLoading } = useMaintenancePlan(id);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && existing) {
    return <EditBranch existing={existing} />;
  }

  return (
    <CreateBranch
      defaultVesselId={searchParams.get('vesselId')}
      defaultEquipmentId={searchParams.get('equipmentId')}
      defaultEquipmentTypeId={searchParams.get('equipmentTypeId')}
    />
  );
}
